import { useEffect } from "react";

/**
 * Toggles `html.zv-keyboard-open` while the mobile soft keyboard is up.
 *
 * Why: the mobile BottomNavBar is `position: fixed; bottom: 0`. When the
 * keyboard opens, iOS Safari / Chrome-iOS (WebKit) shrink + scroll the visual
 * viewport but keep fixed elements pinned to the layout viewport, so the nav
 * gets dragged into the middle of the screen with a white gap under it.
 * Android Chrome (`interactive-widget=resizes-content`) parks it on top of
 * the keyboard. Native apps hide the tab bar while typing — we do the same:
 * CSS keyed off this class hides the bar and zeroes `--bottom-nav-height`,
 * so every layout that reserves nav space collapses with it.
 *
 * Detection — focus alone is NOT enough: iOS does not open the keyboard for a
 * programmatic `.focus()` outside a user gesture (e.g. the chat input's
 * `chatInputAutoFocusPending` 80 ms timer, login/OTP/comment autofocus), so
 * hiding on every focus would make the nav vanish with no keyboard on screen.
 *  - Focus that follows a user touch/key → hide immediately (before iOS animates
 *    the keyboard, so there is no jump), then VERIFY via visualViewport once
 *    the keyboard has had time to appear; if the viewport never shrank, show
 *    the nav again.
 *  - Any other focus → hide only once visualViewport actually shrinks.
 *  - Viewport back to full height while still focused (Android back button
 *    dismisses the keyboard without blurring) → show the nav again.
 */
export const KEYBOARD_CLASS = "zv-keyboard-open";
// Viewport has to shrink at least this much to count as "keyboard up"
// (iOS toolbar collapse/expand is ~80px, a keyboard is 250px+).
const KEYBOARD_MIN_HEIGHT = 150;
// A focus this soon after a touch/key on the field counts as user-initiated.
const USER_GESTURE_WINDOW_MS = 1000;
// Time the keyboard gets to open (and visualViewport to shrink) before we
// decide no keyboard appeared. Closing is also suppressed during this window.
const KEYBOARD_VERIFY_MS = 1200;

const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "date",
  "datetime-local",
  "file",
  "hidden",
  "image",
  "month",
  "radio",
  "range",
  "reset",
  "submit",
  "time",
  "week",
]);

const isTextEntry = (el: Element | null): boolean => {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) {
    return !el.readOnly && !el.disabled && !NON_TEXT_INPUT_TYPES.has((el.type || "text").toLowerCase());
  }
  return (el as HTMLElement).isContentEditable === true;
};

export const useSoftKeyboardClass = (enabled: boolean) => {
  useEffect(() => {
    const root = document.documentElement;
    if (!enabled) {
      root.classList.remove(KEYBOARD_CLASS);
      return;
    }

    const vv = window.visualViewport;
    const currentHeight = () => vv?.height ?? window.innerHeight;
    // Full (keyboard-down) viewport height, refreshed whenever nothing is focused.
    let baselineHeight = currentHeight();
    let lastGestureAt = 0;
    let lastGestureTarget: Node | null = null;
    let lastFocusInAt = 0;
    let verifyTimer: number | undefined;
    let focusOutTimer: number | undefined;

    const setOpen = (open: boolean) => root.classList.toggle(KEYBOARD_CLASS, open);
    const isOpen = () => root.classList.contains(KEYBOARD_CLASS);
    const viewportShrunk = () => currentHeight() < baselineHeight - KEYBOARD_MIN_HEIGHT;

    const handleGesture = (e: Event) => {
      lastGestureAt = Date.now();
      lastGestureTarget = e.target instanceof Node ? e.target : null;
    };

    // User tapped the field itself or its wrapper (e.g. the chat pill) — iOS
    // opens the keyboard for that. A tap elsewhere (a suggestion chip) that
    // focuses the field from a timer does NOT reliably open it.
    const isUserFocus = (field: Element, focusAt: number) => {
      if (focusAt - lastGestureAt > USER_GESTURE_WINDOW_MS) return false;
      // keydown targets the previously focused field (Tab / Next) — still a user action.
      if (!lastGestureTarget || lastGestureTarget === document.body) return true;
      return field.contains(lastGestureTarget) || lastGestureTarget.contains(field) || isTextEntry(lastGestureTarget as Element);
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (!isTextEntry(e.target as Element)) {
        setOpen(false);
        return;
      }
      window.clearTimeout(focusOutTimer);
      // Keyboard still down → current height is the real full height.
      if (!isOpen() && !viewportShrunk()) baselineHeight = currentHeight();
      lastFocusInAt = Date.now();

      if (viewportShrunk()) {
        // Keyboard already up (hopping between fields) — stay hidden.
        setOpen(true);
        return;
      }
      if (isUserFocus(e.target as Element, lastFocusInAt)) {
        setOpen(true);
        window.clearTimeout(verifyTimer);
        verifyTimer = window.setTimeout(() => {
          // No viewport API → can't verify, trust the gesture.
          if (!vv) return;
          if (isTextEntry(document.activeElement) && !viewportShrunk()) setOpen(false);
        }, KEYBOARD_VERIFY_MS);
      }
      // Programmatic focus: wait for handleViewportResize to see a real keyboard.
    };

    // focusout fires before focus lands on the next field — re-check once
    // activeElement has settled so hopping between inputs doesn't flash the nav.
    const handleFocusOut = () => {
      window.clearTimeout(focusOutTimer);
      focusOutTimer = window.setTimeout(() => {
        if (!isTextEntry(document.activeElement)) {
          window.clearTimeout(verifyTimer);
          setOpen(false);
        }
      }, 50);
    };

    const handleViewportResize = () => {
      if (!isTextEntry(document.activeElement)) {
        // Keyboard is down — track the full height (toolbar collapse, rotation).
        if (!isOpen()) baselineHeight = currentHeight();
        return;
      }
      if (viewportShrunk()) {
        setOpen(true);
      } else if (Date.now() - lastFocusInAt > KEYBOARD_VERIFY_MS) {
        // Back to full height while still focused = keyboard dismissed without
        // a blur (Android back button). Mid-open-animation resizes are ignored.
        setOpen(false);
      }
    };

    document.addEventListener("touchstart", handleGesture, { capture: true, passive: true });
    document.addEventListener("pointerdown", handleGesture, { capture: true, passive: true });
    document.addEventListener("keydown", handleGesture, { capture: true });
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);
    vv?.addEventListener("resize", handleViewportResize);

    return () => {
      window.clearTimeout(verifyTimer);
      window.clearTimeout(focusOutTimer);
      document.removeEventListener("touchstart", handleGesture, { capture: true });
      document.removeEventListener("pointerdown", handleGesture, { capture: true });
      document.removeEventListener("keydown", handleGesture, { capture: true });
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
      vv?.removeEventListener("resize", handleViewportResize);
      root.classList.remove(KEYBOARD_CLASS);
    };
  }, [enabled]);
};
